import { useQueryClient } from "@tanstack/react-query";
import { useCallback, useMemo } from "react";
import * as FSQ from "src/generated/hooks/form-service/queries";
import * as AF from "src/types/formTemplate";
import * as RD from "src/types/remoteData";

function useGetFormTemplate(
  formTemplateId: uuid,
  version: "Latest" | "Published" = "Latest"
) {
  const query = FSQ.useGetFormTemplate(
    {
      path: {
        formTemplateId,
      },
      query: {
        version,
      },
    },
    [formTemplateId, version]
  );

  return useMemo(
    () =>
      RD.fromRestResult(query)
        .mapError((error) => error as Error)
        .map(({ data }) => data),
    [query]
  );
}

function useUpdateFormSchema() {
  const { mutateAsync } = FSQ.useUpdateFormSchema();
  const invalidateFormTemplate = useInvalidateFormTemplate();

  return useCallback(
    async (formTemplateId: uuid, formTemplate: AF.V2.FormTemplate) => {
      const response = await mutateAsync({
        path: {
          formTemplateId: formTemplateId,
        },
        body: {
          schema: formTemplate,
        },
      });
      invalidateFormTemplate(formTemplateId);
      return response;
    },
    [mutateAsync, invalidateFormTemplate]
  );
}

function useInvalidateFormTemplate() {
  const queryClient = useQueryClient();
  return useCallback(
    async (formTemplateId?: uuid) => {
      await queryClient.invalidateQueries({
        queryKey: [FSQ.useGetFormTemplateKey, formTemplateId],
      });
    },
    [queryClient]
  );
}

function useImportFromJSON() {
  const { mutateAsync } = FSQ.useImportFormTemplateFromJson();
  const invalidateFormTemplate = useInvalidateFormTemplate();
  return useCallback(
    async (formTemplateId: uuid, formTemplate: AF.V2.FormTemplate) => {
      const response = await mutateAsync({
        path: { formTemplateId },
        body: { schema: formTemplate },
      });
      invalidateFormTemplate(formTemplateId);
      return response;
    },
    [mutateAsync, invalidateFormTemplate]
  );
}

function useImportFromOther() {
  const { mutateAsync } = FSQ.useImportFormTemplateFromOther();
  const invalidateFormTemplate = useInvalidateFormTemplate();
  return useCallback(
    async (formTemplateId: uuid, sourceFormTemplateId: uuid) => {
      const response = await mutateAsync({
        path: { formTemplateId, sourceFormTemplateId },
      });
      invalidateFormTemplate(formTemplateId);
      return response;
    },
    [mutateAsync, invalidateFormTemplate]
  );
}

const client = {
  useGetFormTemplate,
  useUpdateFormSchema,
  useInvalidateFormTemplate,
  useImportFromJSON,
  useImportFromOther,
};

export default client;
